import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { saveAs } from 'file-saver';
import { RefineJob, PromptType } from './RefineTab';

interface RefineOutputProps {
  job: RefineJob;
  noteFileName: string;
  sourceFileName?: string;
  promptType: PromptType;
  isGenerating: boolean;
  onRegenerate: () => void;
  onReset: () => void;
}

const promptTypeLabels: Record<PromptType, string> = {
  enrich: 'Enrich My Notes',
  format: 'Format My Notes',
  citation: 'Add Citations',
  custom: 'Custom Prompt',
};

export function RefineOutput({
  job,
  noteFileName,
  sourceFileName,
  promptType,
  isGenerating,
  onRegenerate,
  onReset,
}: RefineOutputProps) {
  const [copied, setCopied] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);

  const latestOutput = job.outputs[job.outputs.length - 1];
  const currentOutput = selectedVersion !== null
    ? job.outputs.find(o => o.version === selectedVersion) || latestOutput
    : latestOutput;

  const handleCopy = async () => {
    if (!currentOutput) return;

    try {
      await navigator.clipboard.writeText(currentOutput.markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = currentOutput.markdown;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (!currentOutput) return;

    const filename = noteFileName
      ? `${noteFileName.replace(/\.[^/.]+$/, '')}-refined.md`
      : 'refined-notes.md';

    const blob = new Blob([currentOutput.markdown], { type: 'text/markdown;charset=utf-8' });
    saveAs(blob, filename);
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-surface-100">Refined Output</h3>
            <p className="text-sm text-surface-400">
              {job.status === 'processing' ? 'Generating...' : `Version ${currentOutput?.version || 1}`}
              {currentOutput?.tokensUsed && (
                <span className="ml-2 text-surface-500">
                  ({currentOutput.tokensUsed.toLocaleString()} tokens)
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={onReset}
            className="p-2 text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 rounded-lg transition-colors"
            title="Start over"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-surface-800 rounded-lg p-4 border border-surface-700">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-surface-500 block">Notes File</span>
            <span className="text-surface-200 font-medium truncate block">{noteFileName}</span>
          </div>
          <div>
            <span className="text-surface-500 block">Source Document</span>
            <span className="text-surface-200 font-medium truncate block">{sourceFileName || 'None'}</span>
          </div>
          <div>
            <span className="text-surface-500 block">Prompt Used</span>
            <span className="text-surface-200 font-medium">{promptTypeLabels[promptType]}</span>
          </div>
          <div>
            <span className="text-surface-500 block">Versions</span>
            <span className="text-surface-200 font-medium">{job.outputs.length}</span>
          </div>
        </div>
      </div>

      {/* Version Selector (if multiple versions) */}
      {job.outputs.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-surface-400">Version:</span>
          <div className="flex gap-1">
            {job.outputs.map((output) => (
              <button
                key={output.version}
                onClick={() => setSelectedVersion(output.version)}
                className={`
                  px-3 py-1 rounded-lg text-sm font-medium transition-colors
                  ${(selectedVersion === output.version || (selectedVersion === null && output === latestOutput))
                    ? 'bg-primary-500 text-white'
                    : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
                  }
                `}
              >
                v{output.version}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content Display */}
      <div className="relative">
        {job.status === 'processing' ? (
          <div className="bg-surface-800 rounded-xl p-8 border border-surface-700 text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            <p className="text-surface-300">Refining your notes...</p>
            <p className="text-sm text-surface-500 mt-1">This may take up to 30 seconds</p>
          </div>
        ) : currentOutput ? (
          <div className="bg-surface-800 rounded-lg border border-surface-700 overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-2 bg-surface-800 border-b border-surface-700">
              <div className="flex items-center gap-2">
                <span className="text-xs text-surface-500 font-mono">markdown</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleCopy}
                  className="p-1.5 text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 rounded transition-colors flex items-center gap-1.5"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <>
                      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-xs text-green-400">Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="text-xs">Copy</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleDownload}
                  className="p-1.5 text-surface-400 hover:text-surface-200 hover:bg-surface-700/50 rounded transition-colors flex items-center gap-1.5"
                  title="Download as .md"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span className="text-xs">Download</span>
                </button>
              </div>
            </div>

            {/* Markdown Content */}
            <div className="p-6 max-h-[600px] overflow-y-auto">
              <div className="markdown-preview">
                <ReactMarkdown>{currentOutput.markdown}</ReactMarkdown>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-surface-800 rounded-xl p-8 border border-surface-700 text-center">
            <p className="text-surface-400">No output available</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {job.status === 'completed' && (
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={onRegenerate}
            disabled={isGenerating}
            className="btn-secondary flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <div className="w-4 h-4 border-2 border-surface-400/30 border-t-surface-400 rounded-full animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Regenerate (New Version)
              </>
            )}
          </button>
          <button onClick={handleDownload} className="btn-primary flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download .md
          </button>
        </div>
      )}
    </div>
  );
}
